/* eslint no-param-reassign: ["error", { "props": false }] */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Auth } from "aws-amplify";
import { CognitoUser } from "@aws-amplify/auth";
import { AppThunk } from "app/store";
import { fetchUserProfile, getProfileSuccess, clearProfile } from "features/user/reducer";
import config from "config";
import { postDashboardLogin, postSignUp, postAliasAccountPasswordReset } from "api/auth";
import { getClubSuccess } from "features/calendar/reducer";
import { setPersistedData, PersistedData, getPersistedData, removePersistedData } from "utils/persist";
import { getDashboardClub, Club } from "api/clubs";
import { setExternalToken } from "utils/api";

// configure AWS Cognito
Auth.configure(config.cognito);

// auth dialog types
export enum AuthDialog {
    None = "None",
    Login = "Login",
    MFARequired = "MFARequired",
    NewPasswordRequired = "NewPasswordRequired",
    Register = "Register",
    ForgotPassword = "ForgotPassword",
    ConfirmSignup = "ConfirmSignup",
}

interface CodeDeliveryDetails {
    AttributeName: string;
    DeliveryMedium: string;
    Destination: string;
}

export interface UnConfrimedSignup {
    user: CognitoUser;
    userConfirmed: boolean;
    userSub: string;
    codeDeliveryDetails: CodeDeliveryDetails;
}

// auth level types
export enum AuthLevel {
    Unauthorized = "Unauthorized",
    Dashboard = "Dashboard",
    External = "External",
    User = "User",
}

// auth state
interface AuthState {
    error: string | null,
    isLoading: boolean,
    level: AuthLevel,
    showDialog: AuthDialog,
}

// initial state
const initialState: AuthState = {
    error: null,
    isLoading: false,
    level: AuthLevel.Unauthorized,
    showDialog: AuthDialog.None,
};

// generic reducers
const startLoading = (state: AuthState) => {
    state.isLoading = true;
    state.error = null;
};
const loadingFailed = (state: AuthState, { payload }: PayloadAction<string>) => {
    state.isLoading = false;
    state.error = payload;
};

// slice
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        authFailure: loadingFailed,
        authStart: startLoading,
        authSuccess: (state, { payload }: PayloadAction<AuthLevel>) => {
            state.isLoading = false;
            state.level = payload;
            state.showDialog = AuthDialog.None;
        },
        endLoading: (state) => { state.isLoading = false; },
        showDialog: (state, { payload }: PayloadAction<AuthDialog>) => {
            state.error = null; // clear errors
            state.showDialog = payload;
        },
    },
});

// inferred actions
const { authStart, authSuccess, authFailure, endLoading } = authSlice.actions;
export const { showDialog } = authSlice.actions;

// cognito user for challenge responses
let cognitoUser: any = null;

// login with AWS Cognito
export const login = (email: string, password: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line

    // alias account -> append suffix
    if ((email.endsWith(config.aliasAccount.emailSuffix) || email.endsWith(config.aliasAccount.legacyEmailSuffix)) && password.match(config.aliasAccount.pinRegex)) {
        password = `${password}${config.aliasAccount.passwordSuffix}`; // eslint-disable-line
    }

    // regular flow
    try {
        dispatch(authStart());
        cognitoUser = await Auth.signIn(email, password);
        if (cognitoUser.challengeName === "NEW_PASSWORD_REQUIRED") {
            dispatch(endLoading());
            dispatch(showDialog(AuthDialog.NewPasswordRequired));
        } else if (cognitoUser.challengeName === "SOFTWARE_TOKEN_MFA") {
            dispatch(endLoading());
            dispatch(showDialog(AuthDialog.MFARequired));
        } else {
            dispatch(authSuccess(AuthLevel.User));
            dispatch(fetchUserProfile());
        }
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

// login with social signin
export const loginSocial = (provider: "apple" | "google"): AppThunk => () => {
    setPersistedData(PersistedData.AwaitSocialSignIn, "true");
    Auth.federatedSignIn({ provider: (provider === "apple" ? "SignInWithApple" : "Google") as any });
};

// new password entered (complete AWS challenge)
export const completeNewPassword = (password: string, confirm: string): AppThunk => async (dispatch) => {
    try {
        dispatch(authStart());
        if (password.length < 6 || !/[A-Z]/.test(password)) throw { type: "passwordPolicy" }; // eslint-disable-line
        if (password !== confirm) throw { type: "passwordsDontMatch" }; // eslint-disable-line
        cognitoUser = await Auth.completeNewPassword(cognitoUser, password, {});
        dispatch(authSuccess(AuthLevel.User));
        dispatch(fetchUserProfile());
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

// MFA code entered (complete AWS challenge)
export const confirmMFACode = (code: string): AppThunk => async (dispatch) => {
    try {
        dispatch(authStart());
        if (code.length < 6 || !/(\d){6}/.test(code)) throw { type: "mfaCodeFormat" }; // eslint-disable-line

        cognitoUser = await Auth.confirmSignIn(
            cognitoUser,
            code,
            "SOFTWARE_TOKEN_MFA",
        );
        dispatch(authSuccess(AuthLevel.User));
        dispatch(fetchUserProfile());
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

// login as dashboard user
export const loginDashboard = (clubKey: string): AppThunk => async (dispatch) => {
    try {
        dispatch(authStart());
        const { club, token } = await postDashboardLogin(clubKey);
        setPersistedData(PersistedData.DashboardToken, token);
        dispatch(authSuccess(AuthLevel.Dashboard));
        setPersistedData(PersistedData.CurrentClub, club.id);
        dispatch(getClubSuccess(club));
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

// automatically authorize as user
export const autoAuthorize = (): AppThunk => async (dispatch) => {
    try {
        // try user auth (highest priority)
        cognitoUser = await Auth.currentAuthenticatedUser();
        dispatch(authSuccess(AuthLevel.User));
        removePersistedData(PersistedData.AwaitSocialSignIn);
        dispatch(fetchUserProfile());
    } catch (error) {
        // returned from social signin? takes a few moments...
        if (getPersistedData(PersistedData.AwaitSocialSignIn) === "true") {
            setTimeout(() => dispatch(autoAuthorize()), 500);
            return;
        }

        // try dashboard auth (fallback)
        const dashboardToken = getPersistedData(PersistedData.DashboardToken);
        if (dashboardToken) {
            dispatch(authSuccess(AuthLevel.Dashboard));
            const club = await getDashboardClub();
            setPersistedData(PersistedData.CurrentClub, club.id);
            dispatch(getClubSuccess(club));
        }
    }
};

// authorize embedded calendar
export const authorizeEmbedded = (token: string): AppThunk => async (dispatch) => {
    setExternalToken(token);
    dispatch(authSuccess(AuthLevel.Dashboard));
    const club = await getDashboardClub();
    dispatch(getClubSuccess(club));
};

// authorize with external token
export const authorizeExternal = (token: string): AppThunk => async (dispatch) => {
    setExternalToken(token);
    dispatch(authSuccess(AuthLevel.External));
};

// sign out user
export const signOut = (): AppThunk => async (dispatch) => {
    removePersistedData(PersistedData.CurrentClub); // clear current club
    await Auth.signOut();
    dispatch(authSuccess(AuthLevel.Unauthorized));
    dispatch(clearProfile());
};

// logout dashboard (includes user)
export const logoutDashboard = (): AppThunk => (dispatch) => {
    removePersistedData(PersistedData.DashboardToken); // remove token
    dispatch(signOut());
    dispatch(getClubSuccess(null as unknown as Club));
};

// sign up with AWS Cognito
export const signUp = (email: string, password: string, firstName: string, lastName: string, clubId?: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line
    try {
        dispatch(authStart());
        if (!firstName || !lastName) throw { type: "firstOrLastNameEmpty" }; // eslint-disable-line
        if (password.length < 6 || !/[A-Z]/.test(password)) throw { type: "passwordPolicy" }; // eslint-disable-line
        const signUpResult: UnConfrimedSignup = await Auth.signUp({ username: email, password });
        return signUpResult;
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
        return error.type || error.name;
    }
};

// forgot password request
export const forgotPassword = (email: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line
    try {
        dispatch(authStart());
        // alias account -> api call & finished
        if (email.endsWith(config.aliasAccount.emailSuffix) || email.endsWith(config.aliasAccount.legacyEmailSuffix)) {
            await postAliasAccountPasswordReset(email);
            dispatch(showDialog(AuthDialog.Login));
        } else {
            await Auth.forgotPassword(email); // regular flow
        }
        dispatch(endLoading());
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

// complete forgot password
export const completeForgotPassword = (email: string, code: string, password: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line
    try {
        dispatch(authStart());
        if (password.length < 6 || !/[A-Z]/.test(password)) throw { type: "passwordPolicy" }; // eslint-disable-line
        await Auth.forgotPasswordSubmit(email, code, password);
        dispatch(endLoading());
        dispatch(showDialog(AuthDialog.Login));
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
    }
};

export const onResendConfirmationCode = (email: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line
    try {
        dispatch(authStart());
        await Auth.resendSignUp(email);
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
        return error.type || error.name;
    }
    return undefined;
};

export const onConfirmUser = (email: string, password: string, firstName: string, lastName: string, code: string, clubId?: string): AppThunk => async (dispatch) => {
    email = email.toLowerCase(); // eslint-disable-line
    try {
        dispatch(authStart);
        await Auth.confirmSignUp(email, code);
        cognitoUser = await Auth.signIn(email, password);
        dispatch(authSuccess(AuthLevel.User));
        const userProfile = await postSignUp(firstName, lastName, clubId);
        dispatch(getProfileSuccess(userProfile));
    } catch (error: any) {
        dispatch(authFailure(error.type || error.name));
        return error.type || error.name;
    }
    return undefined;
};

export default authSlice.reducer;
